import React, { useState } from 'react';
import {
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Popconfirm,
  Empty,
  Spin,
  Card,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SafetyOutlined,
  UserOutlined,
  TeamOutlined,
  SearchOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { Role, RoleSearchParams, RoleListProps } from '../types';
import { isSystemRole } from '../utils';

const { Search } = Input;

export const RoleList: React.FC<RoleListProps> = ({
  data,
  loading = false,
  onAdd,
  onEdit,
  onDelete,
  onConfigPermission,
  onSearch,
}) => {
  const [searchParams, setSearchParams] = useState<RoleSearchParams>({});

  const handleSearch = (keyword: string) => {
    const params = { ...searchParams, keyword };
    setSearchParams(params);
    onSearch(params);
  };

  const handleTypeChange = (type?: 'system' | 'custom') => {
    const params = { ...searchParams, type };
    setSearchParams(params);
    onSearch(params);
  };

  const handleStatusChange = (status?: 'active' | 'disabled') => {
    const params = { ...searchParams, status };
    setSearchParams(params);
    onSearch(params);
  };

  const columns: ColumnsType<Role> = [
    {
      title: '角色名称',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      fixed: 'left',
      render: (name: string, record: Role) => (
        <Space>
          {record.type === 'system' ? (
            <SafetyOutlined style={{ color: '#1890ff' }} />
          ) : (
            <UserOutlined style={{ color: '#52c41a' }} />
          )}
          <span style={{ fontWeight: 500 }}>{name}</span>
        </Space>
      ),
    },
    {
      title: '角色代码',
      dataIndex: 'code',
      key: 'code',
      width: 220,
      render: (code: string) => (
        <Tag color="blue" style={{ fontFamily: 'monospace' }}>
          {code}
        </Tag>
      ),
    },
    {
      title: '角色类型',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      align: 'center',
      render: (type: 'system' | 'custom') =>
        type === 'system' ? (
          <Tag color="blue">系统角色</Tag>
        ) : (
          <Tag color="green">自定义</Tag>
        ),
    },
    {
      title: '关联用户数',
      dataIndex: 'userCount',
      key: 'userCount',
      width: 120,
      align: 'center',
      render: (count: number) => (
        <span
          style={{
            color: count > 0 ? '#1890ff' : '#999',
            cursor: count > 0 ? 'pointer' : 'default',
          }}
        >
          <TeamOutlined /> {count}
        </span>
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (desc?: string) => desc || <span style={{ color: '#999' }}>-</span>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center',
      render: (status: 'active' | 'disabled') =>
        status === 'active' ? (
          <Tag color="success">启用</Tag>
        ) : (
          <Tag color="default">禁用</Tag>
        ),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (time: string) =>
        new Date(time).toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        }),
    },
    {
      title: '操作',
      key: 'actions',
      width: 220,
      fixed: 'right',
      align: 'center',
      render: (_: unknown, record: Role) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<ToolOutlined />}
            onClick={() => onConfigPermission(record.id)}
            data-testid={`role-btn-permission-${record.id}`}
          >
            配置权限
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => onEdit(record.id)}
            data-testid={`role-btn-edit-${record.id}`}
          >
            编辑
          </Button>
          {!isSystemRole(record.type) && (
            <Popconfirm
              title="确认删除"
              description={`确定要删除角色"${record.name}"吗？`}
              onConfirm={() => onDelete(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                data-testid={`role-btn-delete-${record.id}`}
              >
                删除
              </Button>
            </Popconfirm>
          )}
          {isSystemRole(record.type) && (
            <span style={{ color: '#999', fontSize: 12 }}>
              系统角色
            </span>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        {/* 搜索和筛选栏 */}
        <Space
          direction="vertical"
          size="middle"
          style={{ width: '100%', marginBottom: 16 }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Space>
              <Search
                placeholder="搜索角色名称或代码"
                allowClear
                style={{ width: 300 }}
                onSearch={handleSearch}
                prefix={<SearchOutlined />}
                data-testid="role-input-search"
              />
              <Select
                placeholder="角色类型"
                allowClear
                style={{ width: 140 }}
                onChange={handleTypeChange}
                options={[
                  { label: '系统角色', value: 'system' },
                  { label: '自定义角色', value: 'custom' },
                ]}
                data-testid="role-select-type"
              />
              <Select
                placeholder="状态"
                allowClear
                style={{ width: 120 }}
                onChange={handleStatusChange}
                options={[
                  { label: '启用', value: 'active' },
                  { label: '禁用', value: 'disabled' },
                ]}
                data-testid="role-select-status"
              />
            </Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={onAdd}
              data-testid="role-btn-add"
            >
              新建角色
            </Button>
          </div>
        </Space>

        {/* 数据表格 */}
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={data}
            rowKey="id"
            data-testid="role-table"
            pagination={{
              total: data.length,
              pageSize: 10,
              showTotal: (total) => `共 ${total} 条记录`,
              showSizeChanger: true,
              showQuickJumper: true,
            }}
            scroll={{ x: 1400 }}
            locale={{
              emptyText: (
                <Empty
                  description="暂无角色数据"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ),
            }}
          />
        </Spin>
      </Card>
    </div>
  );
};
